import Image from "next/image"
import { ArticleDetailProps } from "./constants"
import Link from "next/link";
import { ArticleJsonLd, NextSeo } from "next-seo";
import RelatedArticle from "./RelatedArticle";
import { useFocusProductStore } from "@/stores/useFoucsProductStore";
import { useEffect, useState } from "react";
import { getFocusProducts, updateArticle } from "@/lib/Definitions";
import SlideFocusProduct from "../Product/SlideFocusProduct";
import HorizontalProduct from "../Product/HorizontalProduct";
import MinArticle from "./MinArticle";
import { useRouter } from "next/router";
import { FacebookShareButton, LineShareButton, WhatsappShareButton } from "react-share";
import FreeConsultation from "../Common/FreeConsultation";


interface ArticleDetailItemProps {
  article: ArticleDetailProps;
}

const Article = (props: ArticleDetailItemProps) => {
  const router = useRouter();
  const { article } = props;
  const { focusProduct, updateFocusProduct, getFocusProduct } = useFocusProductStore();
  const [focusProductData, setFocusProductData] = useState(getFocusProduct());
  const [authorImage, setAuthorImage] = useState<string>(process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/mamapedia-author.png");
  const [authorSize, setAuthorSize] = useState<number>(14);

  useEffect(() => {
    if (focusProductData.data.length === undefined || focusProductData.data.length === 0 || focusProductData.update_date === null || focusProductData.update_date < Date.now() - 3600000) {
      getFocusProducts()
        .then((response) => {
          const data = {
            update_date: Date.now(),
            data: response
          }
  
          updateFocusProduct(data);
          setFocusProductData(data);
        })
    }
  }, [focusProductData, updateFocusProduct])

  useEffect(() => {
    setFocusProductData(getFocusProduct());
  }, [focusProduct, getFocusProduct])

  useEffect(() => {
    const handleRouteChange = () => {
      updateArticle(article.slug);

      if (article.author_name === "Ayu Citra Gestari") {
        setAuthorImage(process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/author/ayu-citra.png");
        setAuthorSize(19);
      } else {
        setAuthorImage(process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/mamapedia-author.png");
        setAuthorSize(14);
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    };
  }, [article.slug, router.events]);

  let matchCount = 0;

  return (
    <>
      <NextSeo
        title=''
        defaultTitle={article.meta_title}
        description={article.meta_description}
        canonical={process.env.NEXT_PUBLIC_HOSTNAME + "/mamapedia/" + article.slug}
        openGraph={{
          images: [{
            url:article.main_image && article.main_image.length > 0 ? article.main_image : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/brand/logo-mooimom-middle.png",
            width: 800,
            height: 400,
            alt: article.title + " image",
          }]
        }}
      />
      <ArticleJsonLd
        url={process.env.NEXT_PUBLIC_HOSTNAME + "/mamapedia/" + article.slug}
        title={article.title}
        images={[article.main_image]}
        datePublished={article.date}
        dateModified={article.date}
        authorName={article.author_name}
        publisherName="MOOIMOM"
        publisherLogo={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/logo_w2.png"}
        description={article.meta_description}
      />
      <div className="container grid grid-cols-1 gap-8 text-sm text-black mamapedia-article font-poppins md:grid-cols-3 md:mt-8">
        <div className="col-span-1 md:col-span-2">
          <h1 className="mt-5 mb-4 text-2xl font-bold md:text-3xl">{article.title}</h1>
          <div className="flex flex-wrap space-x-4 md:mb-6">
            <div className="flex items-center space-x-2">
              <Image
                src={authorImage}
                alt="author icon"
                quality={100}
                width={authorSize}
                height={authorSize}
              />
              <p className="text-sm font-bold underline">{article.author_name}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Image
                src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/mamapedia-calendar.png"}
                alt="calendar icon"
                quality={100}
                width={12}
                height={12}
              />
              <p className="text-sm">{article.date}</p>
            </div>
            <div className="items-center hidden space-x-2 md:flex">
              <Image
                src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/pencil.png"}
                alt="category icon"
                quality={100}
                width={14}
                height={14}
              />
              <p className="text-xs md:text-sm text-mooimom">{article.category}</p>
            </div>
          </div>
          <div className="flex items-center mt-4 mb-6 space-x-2 md:hidden">
            <Image
              src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/pencil.png"}
              alt="category icon"
              quality={100}
              width={10}
              height={10}
            />
            <p className="text-xs md:text-sm text-mooimom">{article.category}</p>
          </div>
          <Image
            src={article.main_image}
            alt={article.title}
            quality={100}
            width={800}
            height={400}
          />
          <div className="text-[13px] mt-5 mamapedia-content" dangerouslySetInnerHTML={{
            __html: article.content.replace(/\$\[banner_single\]\$/g, (match) => {
              if (article.banner_list && article.banner_list[matchCount]) {
                const banner = article.banner_list[matchCount];
                matchCount++;
                return `<a href="${banner.link}"><img src="${banner.desktop_image}" class="desktop-banner" alt="banner" /><img src="${banner.desktop_image}" class="mobile-banner" alt="banner" /></a>`;
              } else {
                return '';
              }
            }).replace(/\$\[video\((.*?)\)\]\$/g, (match, videoId) => {
              return `<iframe width="640" height="360" src="https://www.youtube.com/embed/${videoId.replace(/<u>|<\/u>/g, '')}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
            }).replace('src="/media', 'src="https://static.mooimom.id/media').replace('src="https://www.mooimom.id/media', 'src="https://static.mooimom.id/media')
          }}></div>
          {article.tags.length > 0 && (
            <div className="flex flex-wrap mt-10 tags">
              {article.tags.map((tag, index) => (
                <div key={index} className="mb-[10px] mr-[10px]">
                  <Link href={"/latest-mamapedia/all-mamapedia?tag=" + tag}>
                    <div key={index} className="px-2 py-1 text-xs bg-gray-200 rounded-md md:text-sm tag">{tag}</div>
                  </Link>
                </div>
              ))}
            </div>
          )}
          <hr className="my-4 bg-reviewBg" />
          <p className="text-sm font-medium text-center text-black">Bagikan Artikel</p>
          <div className="flex items-center justify-center mt-2 space-x-6">
            <WhatsappShareButton
              url={article.urls.WhatsApp}
              title={`Yuk, ketahui info terbaru seputar Moms Life di Mamapedia! Click : ${article.urls.WhatsApp}`}
            >
              <Image
                src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/mamapedia-whatsapp.png"}
                alt="whatsapp icon"
                quality={100}
                width={24}
                height={24}
              />
            </WhatsappShareButton>
            <LineShareButton url={article.urls.Line}>
              <Image
                src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/mamapedia-line.png"}
                alt="line icon"
                quality={100}
                width={24}
                height={24}
              />
            </LineShareButton>
            <FacebookShareButton url={article.urls.Facebook}>
              <Image
                src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/mamapedia-facebook.png"}
                alt="facebook icon"
                quality={100}
                width={24}
                height={24}
              />
            </FacebookShareButton>
          </div>
          <hr className="my-4 bg-reviewBg" />
        </div>
        <div className="sticky hidden col-span-1 p-6 border border-solid rounded-lg md:block top-20 h-fit border-reviewBg">
          <div className="w-2/3 p-2 mb-4 text-sm font-bold text-center text-white rounded-md bg-mooimom">TRENDING PRODUK</div>
          {focusProductData.data.length > 0 && (
            focusProductData.data.map((product, index) => (
              <div key={index} className="mb-3">
                <HorizontalProduct product={product} fullWidth={true}  />
              </div>
            ))
          )}
        </div>
      </div>
      <div className="container my-6 font-poppins">
        {article.related_articles && (<h2 className="mb-4 text-lg font-bold md:text-xl">Artikel Terkait</h2>)}
        <div className="grid grid-cols-2 gap-6 md:hidden">
          {article.related_articles && article.related_articles.slice(0, 6).map((related_article, index) => (
            <RelatedArticle key={index} article={related_article} />
          ))}
          {focusProductData.data.length > 0 && (
            <div className="col-span-2 py-4">
              {article.related_articles && (<hr className="mb-6 bg-reviewBg" />)}
              <SlideFocusProduct title="Trending Produk" link="/" link_name="Shop at MOOIMOM" products={focusProductData.data} />
              {article.related_articles && (<hr className="mb-6 bg-reviewBg" />)}
            </div>
          )}
          {article.related_articles && article.related_articles.length > 6 && (
            article.related_articles.slice(6).map((related_article, index) => (
              <RelatedArticle key={index} article={related_article} />
            ))
          )}
        </div>
        <div className="hidden grid-cols-3 gap-6 md:grid">
          {article.related_articles && article.related_articles.map((related_article, index) => (
            <MinArticle key={index} article={related_article} option="A" />
          ))}
        </div>
      </div>
      {article.related_products.length > 0 && (
        <div className="container font-poppins">
          <hr className="my-6 bg-reviewBg" />
          <SlideFocusProduct title="Produk Terkait" link="/" link_name="Shop at MOOIMOM" products={article.related_products} />
          <hr className="mt-6 mb-1 bg-reviewBg" />
        </div>
      )}
      <FreeConsultation />
    </>
  )
}

export default Article
